import { SimpleErrorBoundary } from '@crehana/layouts';

import { useCourseDetailTranslation } from '../../hooks/useCourseDetailTranslation';
import { EnrollmentsCurrentCourseError } from './components/enrollments-current-course-error';
import { EnrollmentsCurrentCourseInfo } from './components/leader-enrollments-course-info';
import { useTotalEnrollmentsByCourse } from './hooks/use-total-enrollments-by-course';
import { EnrollmentsCourseContainerCard } from './ui/enrollments-course-container-card';
import { EnrollmentsCurrentCourseEmpty } from './ui/leader-enrollments-course-empty';

type TEnrollmentsCurrentCourseProps = {
  courseId: string;
  courseTitle: string;
  organizationSlug: string;
  className?: string;
  withCardContainer?: boolean;
};

export const EnrollmentsCurrentCourse: React.FC<
  TEnrollmentsCurrentCourseProps
> = ({
  courseId,
  courseTitle,
  organizationSlug,
  className,
  withCardContainer = true,
}) => {
  const { t } = useCourseDetailTranslation();
  const {
    isEmptyEnrollments,
    loading,
    error,
    isErrorByFragment,
    retryFetchEnrollments,
  } = useTotalEnrollmentsByCourse({
    courseId: Number(courseId),
  });

  if (loading) {
    return null;
  }

  const renderContent = () => {
    if (error || isErrorByFragment) {
      return <EnrollmentsCurrentCourseError onRetry={retryFetchEnrollments} />;
    }

    if (isEmptyEnrollments) {
      return <EnrollmentsCurrentCourseEmpty />;
    }

    return (
      <EnrollmentsCurrentCourseInfo
        courseId={courseId}
        courseTitle={courseTitle}
        organizationSlug={organizationSlug}
      />
    );
  };

  if (withCardContainer) {
    return (
      <EnrollmentsCourseContainerCard
        title={t('ENROLLMENTS_CURRENT_COURSE_DIALOG_TITLE')}
        className={className}
        data-testid="enrollments-current-course-container-card"
      >
        <SimpleErrorBoundary>{renderContent()}</SimpleErrorBoundary>
      </EnrollmentsCourseContainerCard>
    );
  }

  return (
    <section
      className={className}
      data-testid="enrollments-current-course-empty"
    >
      <SimpleErrorBoundary>{renderContent()}</SimpleErrorBoundary>
    </section>
  );
};
